import { IconChart2 } from 'central-icons/IconChart2';
import { IconWindowCursor } from 'central-icons/IconWindowCursor';

import { FeatureFlag } from '@revelio/core';
import type { UploadFileResult } from '@revelio/data-access';
import { MenuItem } from '@revelio/ui';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { usePageExport } from '../Chart/chart-menu/PageExportProvider';
import { usePageConfig } from '../PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../PageConfigProvider/types';
import { useExportCover } from './useExportCover';

// The Radix menu unmounts its content (this component) when the menu closes,
// so the preview modal cannot live here — it would unmount before the async
// upload resolves. The parent owns the modal; this item only reports the
// upload result up via `onPreviewReady` (the closure outlives the unmount).
export const PptExportMenuItem = ({
  onPreviewReady,
}: {
  onPreviewReady: (result: UploadFileResult) => void;
}) => {
  const { exportAllCharts, previewAllCharts } = usePageExport();
  const { config: pageConfig } = usePageConfig();
  const isPPTPreviewEnabled = useUnleashFlag(FeatureFlag.PPTPreview);
  const cover = useExportCover();

  const handleExportAllCharts = async () => {
    try {
      await exportAllCharts({ cover });
    } catch (error) {
      console.error('Error exporting all charts:', error);
    }
  };

  const handleEditAllCharts = async () => {
    try {
      const url = await previewAllCharts({ cover });
      if (url) {
        onPreviewReady(url);
      }
    } catch (e) {
      console.error('Error previewing/editing dashboard PPT:', e);
    }
  };

  const isMenuItemVisible =
    pageConfig.revelioPageId !== RevelioPageId.TALENT_DISCOVERY &&
    !isScreenerV2Page(pageConfig.revelioPageId);

  if (!isMenuItemVisible) {
    return null;
  }

  return (
    <>
      <MenuItem onClick={handleExportAllCharts} icon={<IconChart2 size={16} />}>
        Download PPT
      </MenuItem>
      {isPPTPreviewEnabled && (
        <MenuItem
          onClick={handleEditAllCharts}
          icon={<IconWindowCursor size={16} />}
        >
          Preview PPT
        </MenuItem>
      )}
    </>
  );
};
